/*
 * Copyright 2020 Ladifire. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

import * as React from 'react';
import {ClearSlots, useDOMRef, useSlotProps, useStyleProps} from '@ladifire-ui-react/utils';
import {ContentProps} from '@react-ui-types/view';
import {DOMRef} from '@react-ui-types/shared';
import {filterDOMProps} from '@react-ui-aria/utils';
import {useScrollTrigger} from '@ladifire-ui-react/utils';

import stylex from "@ladifire/stylex";

type Style =
  | 'root';

const styles = stylex.create<Style>({
  root: {
    width: '100%',
    height: '100%',
    overflowX: 'hidden',
    overflowY: 'auto',
  },
});

export interface ContentWithScrollTriggerProps extends ContentProps {
  onScrolledChange?: (scrolled: boolean) => void;
  threshold?: number;
  disableHysteresis?: boolean;
}

function ContentWithScrollTrigger(props: ContentWithScrollTriggerProps, ref: DOMRef) {
  props = useSlotProps(props, 'content');
  let {
    children,
    threshold = 0,
    disableHysteresis = true,
    onScrolledChange,
    ...otherProps
  } = props;
  let {styleProps} = useStyleProps(otherProps);
  let domRef = useDOMRef(ref);

  let scrollContainerRef = React.useRef();

  const scrolled = useScrollTrigger({
    target: scrollContainerRef && scrollContainerRef.current,
    disableHysteresis: disableHysteresis,
    threshold: threshold,
  });

  React.useEffect(() => {
    if (typeof onScrolledChange === 'function') {
      onScrolledChange(scrolled);
    }
  }, [scrolled]);

  return (
    <section
      {...filterDOMProps(otherProps)}
      {...styleProps} ref={domRef}
      style={{overflow: 'hidden'}}
    >
      <div
        ref={scrollContainerRef}
        className={stylex(styles.root)}
      >
        <ClearSlots>
          {children}
        </ClearSlots>
      </div>
    </section>
  );
}

/**
 * Content represents the primary content within a Spectrum container.
 */
const _ContentWithScrollTrigger = React.forwardRef(ContentWithScrollTrigger);
export {_ContentWithScrollTrigger as ContentWithScrollTrigger};
